/*
Форма сброса пароля (вызывается по ссылке из почты) и ввода нового пароля.

KMP-46: В рамках изменения регистрации новой организации при наличии поля описания ошибки смены пароля от сервера
(error_description) выводится он вместо безликого "Ошибка.". Ранее обнаруженная ошибка: (5)"Не найден(т) лицевой счёт".
*/

// *** NPM ***
import React, {useRef, useState} from 'react';
import {Button, FormControl, FormHelperText, IconButton, Input, InputAdornment, InputLabel, makeStyles} from '@material-ui/core';
import {Visibility, VisibilityOff} from '@material-ui/icons';
import {Link, useParams} from 'react-router-dom';

// *** OTHER ***
import logo from '../../img/logo-dark.png';
import {IResetPassword} from '../../interfaces/fetch/resetPassword';
import instance from '../../instance';

const useStyles = makeStyles({
    page: {
        width: '100%',
        height: '100vh',
        backgroundColor: '#3766c2',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Roboto, sans-serif',
    },
    window: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '320px',
        height: '400px',
        backgroundColor: '#fff',
        borderRadius: '5px',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '10px',
    },
    resize: {
        fontSize: '14px',
    },
    input: {
        marginBottom: '28px',
        height: '45px',
    },
    submitButton: {
        width: '270px',
        height: '46px',
        borderRadius: '30px',
        marginTop: '10px',
        marginBottom: '10px',

        backgroundColor: '#3766c2',
        fontSize: '14px',
        textTransform: 'none',
    },
    logo: {
        padding: '24px 24px 0 24px',
    },
    title: {
        margin: '0',
        fontSize: '18px',
        marginTop: '13px',
    },
    postTitle: {
        fontSize: '14px',
        marginTop: '10px',
    },
    helperLinks: {
        fontSize: '14px',
        color: 'rgba(0,0,0,0.38)',
        textDecoration: 'none',
        marginTop: '5px',
    },
    notice: {
        textAlign: 'center',
        margin: '20px auto 30px auto',
    },
});

// *** INTERFACES
interface IChangePassword {
    newPassword: string;
    confirmNewPassword: string;
}

const ConfirmResetPassword = (): React.JSX.Element => {
    const styles = useStyles();
    const {token} = useParams<{token?: string}>();
    const ERROR_STATE_DEFAULT = {
        newPassword: '',
        confirmNewPassword: '',
    };

    // *** HOOKS ***
    const [valuesPassword, setValuesPassword] = useState({
        amount: '',
        password: '',
        weight: '',
        weightRange: '',
        showPassword: false,
    });
    const [isSuccess, setSuccess] = useState<boolean>(false);

    // *** REFS ***
    const newPasswordInputRef = useRef<HTMLInputElement | null>(null);
    const confirmNewPasswordInputRef = useRef<HTMLInputElement | null>(null);

    const [valuesConfirmPassword, setValuesConfirmPassword] = useState({
        amount: '',
        password: '',
        weight: '',
        weightRange: '',
        showPassword: false,
    });
    const [errorState, setErrorState] = useState<IChangePassword>(ERROR_STATE_DEFAULT);

    const handleChange = (prop: any, whichPassword: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (whichPassword === 'password') {
            setValuesPassword({...valuesPassword, [prop]: event.target.value});
        } else {
            setValuesConfirmPassword({...valuesPassword, [prop]: event.target.value});
        }
    };

    const handleClickShowPassword = (whichPassword: string) => {
        if (whichPassword === 'password') {
            setValuesPassword({...valuesPassword, showPassword: !valuesPassword.showPassword});
        } else {
            setValuesConfirmPassword({
                ...valuesConfirmPassword,
                showPassword: !valuesConfirmPassword.showPassword,
            });
        }
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleForForm = () => {
        if (!newPasswordInputRef.current || !confirmNewPasswordInputRef.current) {
            return;
        }
        const isValid = validationData(newPasswordInputRef.current.value, confirmNewPasswordInputRef.current.value);
        if (isValid) {
            const requestResetPassword: IResetPassword = {
                method: 'RestorePassword',
                params: {
                    link: String(token),
                    password: newPasswordInputRef.current.value,
                },
            };
            void fetchResetPasswordRequest(requestResetPassword);
        }
    };

    const validationData = (newPassword: string, confirmNewPassword: string) => {
        let isValid = true;
        const error = {...ERROR_STATE_DEFAULT};
        if (newPassword !== confirmNewPassword) {
            isValid = false;
            error.newPassword = 'Пароли должны совпадать';
            error.confirmNewPassword = 'Пароли должны совпадать';
        }
        setErrorState(error);
        return isValid;
    };

    const fetchResetPasswordRequest = async (request: IResetPassword) => {
        try {
            const response = await instance.post('/LK', request);
            if (response.data.error === 0) {
                setSuccess(true);
            } else if (response.data.error_description !== undefined && response.data.error_description.length > 0) {
                // NOTE(vmunt): Проверка на undefined работает, проверка на строковый тип error_description не делалась.
                setErrorState({
                    ...errorState,
                    confirmNewPassword: response.data.error_description,
                });
            } else {
                setErrorState({...errorState, confirmNewPassword: 'Ошибка.'});
            }
        } catch {}
    };

    return (
        <div className={styles.page}>
            <div className={styles.window}>
                <img alt="logo" className={styles.logo} src={logo}></img>
                <h1 className={styles.title}>Портall</h1>
                <span className={styles.postTitle}>Сброс пароля</span>
                {isSuccess ? (
                    <span className={styles.notice}>Пароль успешно обновлен</span>
                ) : (
                    <>
                        <form
                            className={styles.form}
                            onSubmit={e => {
                                e.preventDefault();
                                handleForForm();
                            }}
                        >
                            <FormControl className={styles.input} error={errorState.newPassword.length > 0}>
                                <InputLabel htmlFor="standard-adornment-password" className={styles.resize}>
                                    Новый пароль
                                </InputLabel>
                                <Input
                                    id="standard-adornment-password"
                                    type={valuesPassword.showPassword ? 'text' : 'password'}
                                    inputRef={newPasswordInputRef}
                                    value={valuesPassword.password}
                                    required={true}
                                    autoComplete="new-password"
                                    onChange={handleChange('password', 'password')}
                                    onFocus={() => setErrorState({...errorState, newPassword: ''})}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => handleClickShowPassword('password')}
                                                onMouseDown={handleMouseDownPassword}
                                            >
                                                {valuesPassword.showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                <FormHelperText>{errorState.newPassword}</FormHelperText>
                            </FormControl>

                            <FormControl className={styles.input} error={errorState.confirmNewPassword.length > 0}>
                                <InputLabel htmlFor="standard-adornment-password" className={styles.resize}>
                                    Новый пароль еще раз
                                </InputLabel>
                                <Input
                                    id="standard-adornment-password"
                                    type={valuesConfirmPassword.showPassword ? 'text' : 'password'}
                                    inputRef={confirmNewPasswordInputRef}
                                    value={valuesConfirmPassword.password}
                                    required={true}
                                    autoComplete="new-password"
                                    onChange={handleChange('password', 'confirmPassword')}
                                    onFocus={() => setErrorState({...errorState, confirmNewPassword: ''})}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => handleClickShowPassword('confirmPassword')}
                                                onMouseDown={handleMouseDownPassword}
                                            >
                                                {valuesConfirmPassword.showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                />
                                <FormHelperText>{errorState.confirmNewPassword}</FormHelperText>
                            </FormControl>
                            <Button className={styles.submitButton} variant="contained" type="submit" color="primary">
                                Обновить пароль
                            </Button>
                        </form>
                    </>
                )}
                <Link to="/" className={styles.helperLinks}>
                    Войти
                </Link>
            </div>
        </div>
    );
};

export default ConfirmResetPassword;
